import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Plus, Shield, Mail, Send, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/admin/data-table';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuditLogger } from '@/hooks/use-audit-logger';

interface AdminUser {
  id: string;
  full_name?: string;
  email?: string;
  role: string;
  created_at: string;
  assigned_at: string;
}

interface PendingInvite {
  id: string;
  email: string;
  full_name: string;
  global_role: string;
  created_at: string;
  expires_at: string;
}

const UsersPage = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { logAction } = useAuditLogger();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', fullName: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [removeUserId, setRemoveUserId] = useState<string | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const { data: users, isLoading } = useQuery({
    queryKey: ['global-admin-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_portal_admins');

      if (error) throw error;

      return (data || []).map(item => ({
        id: item.user_id,
        full_name: item.full_name,
        email: item.email,
        role: item.role,
        created_at: item.created_at,
        assigned_at: item.assigned_at
      })) as AdminUser[];
    }
  });

  const { data: pendingInvites, refetch: refetchInvites } = useQuery({
    queryKey: ['global-admin-invitations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('global_admin_invitations')
        .select('*')
        .eq('status', 'sent')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as PendingInvite[] || [];
    }
  });

  const handleInvite = async () => {
    if (!inviteData.email || !inviteData.fullName) {
      toast({
        title: 'Erro',
        description: 'Preencha todos os campos',
        variant: 'destructive',
      });
      return;
    }

    setInviteLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-invite-global-user', {
        body: {
          email: inviteData.email.trim(),
          fullName: inviteData.fullName.trim(),
          globalRole: 'portal_admin'
        }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erro ao enviar convite');

      // Log audit
      await logAction({
        action: 'user.invite',
        entity_type: 'user',
        new_values: {
          email: inviteData.email.trim(),
          fullName: inviteData.fullName.trim(),
          globalRole: 'portal_admin'
        }
      });

      toast({
        title: 'Convite enviado!',
        description: `Um email foi enviado para ${inviteData.email}`,
      });

      setInviteModalOpen(false);
      setInviteData({ email: '', fullName: '' });
      queryClient.invalidateQueries({ queryKey: ['global-admin-users'] });
      refetchInvites();

    } catch (error) {
      console.error('Error sending invite:', error);
      toast({
        title: 'Erro',
        description: (error as Error).message || 'Erro ao enviar convite',
        variant: 'destructive',
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvite = async (invitationId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('resend-global-invitation', {
        body: { invitationId }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erro ao reenviar convite');

      toast({
        title: 'Convite reenviado!',
        description: 'Um novo email foi enviado'
      });
    } catch (error) {
      console.error('Error resending invite:', error);
      toast({
        title: 'Erro',
        description: (error as Error).message || 'Erro ao reenviar convite',
        variant: 'destructive'
      });
    }
  };

  const handleRemoveAdmin = async (userId: string) => {
    if (!userId) return;
    
    setRemoveLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-global-admin', {
        body: { userId }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erro ao remover administrador');

      await logAction({
        action: 'user.role_change',
        entity_type: 'user',
        entity_id: userId,
        old_values: { role: 'portal_admin' },
        new_values: { role: null }
      });

      toast({
        title: 'Administrador removido',
        description: 'O acesso de administrador global foi revogado com sucesso',
      });

      setRemoveUserId(null);
      queryClient.invalidateQueries({ queryKey: ['global-admin-users'] });

    } catch (error) {
      console.error('Error removing admin:', error);
      toast({
        title: 'Erro',
        description: (error as Error).message || 'Erro ao remover administrador',
        variant: 'destructive',
      });
    } finally {
      setRemoveLoading(false);
    }
  };

  const columns: ColumnDef<AdminUser>[] = [
    {
      accessorKey: 'full_name',
      header: 'Usuário',
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex items-center space-x-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback>
                <Shield className="h-4 w-4 text-primary" />
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium">{user.full_name || 'Sem nome'}</div>
              <div className="text-sm text-muted-foreground">{user.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'role',
      header: 'Papel',
      cell: () => {
        return (
          <Badge className="bg-destructive/10 text-destructive">
            <Shield className="h-3 w-3 mr-1" />
            Admin Global
          </Badge>
        );
      },
    },
    {
      accessorKey: 'created_at',
      header: 'Data de Criação',
      cell: ({ row }) => {
        const date = new Date(row.getValue('created_at'));
        return (
          <div className="text-sm">
            <div>{date.toLocaleDateString('pt-BR')}</div>
            <div className="text-xs text-muted-foreground">
              {date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const user = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Abrir menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Ações</DropdownMenuLabel>
              <DropdownMenuItem>
                Ver detalhes
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="text-destructive"
                onClick={() => setRemoveUserId(user.id)}
              >
                Remover acesso
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Administradores Globais</h1>
          <p className="text-muted-foreground">
            Gerencie os administradores com acesso total ao sistema
          </p>
        </div>
        <Button onClick={() => setInviteModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Convidar Admin
        </Button>
      </div>

      {/* Convites Pendentes */}
      {pendingInvites && pendingInvites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Clock className="h-5 w-5 mr-2 text-orange-500" />
              Convites Pendentes
            </CardTitle>
            <CardDescription>
              Aguardando aceite dos novos administradores
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingInvites.map(invite => (
              <div key={invite.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback>
                      <Mail className="h-5 w-5 text-muted-foreground" />
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{invite.full_name}</p>
                    <p className="text-sm text-muted-foreground">{invite.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Expira em: {new Date(invite.expires_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleResendInvite(invite.id)}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Reenviar Email
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tabela de Admins */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Administradores Ativos</CardTitle>
          <CardDescription>
            Lista de todos os administradores globais do sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex space-x-4 animate-pulse">
                  <div className="h-10 w-10 bg-muted rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded w-32 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-48"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={users || []}
              searchKey="full_name"
              searchPlaceholder="Buscar administradores..."
            />
          )}
        </CardContent>
      </Card>

      {/* AlertDialog de Confirmação de Remoção */}
      <AlertDialog open={!!removeUserId} onOpenChange={(open) => !open && setRemoveUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Administrador Global</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação irá revogar permanentemente o acesso de administrador global deste usuário.
              <br /><br />
              <strong>Esta ação não pode ser desfeita.</strong> O usuário precisará ser convidado novamente para recuperar o acesso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => removeUserId && handleRemoveAdmin(removeUserId)}
              disabled={removeLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeLoading ? 'Removendo...' : 'Remover Acesso'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Convite */}
      <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Shield className="h-5 w-5 mr-2 text-primary" />
              Convidar Administrador Global
            </DialogTitle>
            <DialogDescription>
              Envie um convite para um novo administrador com acesso total ao sistema.
              Um email será enviado com instruções para criar a conta.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Nome Completo *
              </Label>
              <Input
                id="name"
                value={inviteData.fullName}
                onChange={(e) => setInviteData(prev => ({ ...prev, fullName: e.target.value }))}
                placeholder="Nome completo do administrador"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">
                E-mail *
              </Label>
              <Input
                id="email"
                type="email"
                value={inviteData.email}
                onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@exemplo.com"
              />
            </div>
            <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Atenção:</strong> Este usuário terá acesso total ao sistema, incluindo gerenciamento de empresas, planos e configurações.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setInviteModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleInvite} disabled={inviteLoading}>
              {inviteLoading ? 'Enviando...' : 'Enviar Convite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
